import {createBrowserRouter, type LoaderFunctionArgs, redirect} from "react-router-dom";

import App from "./app/App";
import AppError from "./app/AppError";
import TabAppreciations from "./app/audit/TabAppreciations";
import TabChangelog from "./app/audit/TabChangelog";
import TabDocuments from "./app/audit/TabDocuments";
import TabCadastreLocation from "./app/location/TabCadastreLocation";
import TabClimaterisk from "./app/location/TabClimaterisk";
import TabEmission from "./app/location/TabEmission";
import TabMacroLocation from "./app/location/TabMacroLocation";
import TabMicroLocation from "./app/location/TabMicroLocation";
import TabPoisLocation from "./app/location/TabPoisLocation";
import {TabMarketData} from "./app/market/TabMarketData";
import {TabMarketDescription} from "./app/market/TabMarketDescription";
import TabMarketPriceindex from "./app/market/TabMarketPriceindex";
import TabTimeLines from "./app/market/TabTimelines";
import TabObjectEnergy from "./app/object/TabObjectEnergy";
import TabObjectFinalize from "./app/object/TabObjectFinalize";
import TabObjectGroundvalue from "./app/object/TabObjectGroundvalue";
import TabObjectImmodrop from "./app/object/TabObjectImmodrop";
import TabObjectInitial from "./app/object/TabObjectInitial";
import TabObjectInspection from "./app/object/TabObjectInspection";
import TabObjectOverview from "./app/object/TabObjectOverview";
import TabObjectRegister from "./app/object/TabObjectRegister";
import PageAddress from "./app/PageAddress";
import PageAudit from "./app/PageAudit";
import PageChat from "./app/PageChat";
import PageLocation from "./app/PageLocation";
import PageMarket from "./app/PageMarket";
import PageObject from "./app/PageObject";
import PageOrder from "./app/PageOrder";
import PageStart from "./app/PageStart";
import PageValue from "./app/PageValue";
import AdminTab from "./app/start/AdminTab";
import DoneTab from "./app/start/DoneTab";
import PoolTab from "./app/start/PoolTab";
import ValuationsTable from "./app/start/ValuationsTable";
import TabComparativeValueIndication from "./app/value/TabComparativeValueIndication";
import TabEnergyValueIndication from "./app/value/TabEnergyValueIndication";
import TabIncomeValueIndication from "./app/value/TabIncomeValueIndication";
import TabRealAssetValueIndication from "./app/value/TabRealAssetValueIndication";
import TabValueIndication from "./app/value/TabValueIndication";
import {
    checkPermissionRule,
    PERMISSION_RULE_APPRECIATIONS,
    PERMISSION_RULE_CADASTRE,
    PERMISSION_RULE_CHAT,
    PERMISSION_RULE_CLIMATERISK,
    PERMISSION_RULE_CORRECTION,
    PERMISSION_RULE_EMISSION_TAB,
    PERMISSION_RULE_LOCATION_MACRO,
    PERMISSION_RULE_LOCATION_MICRO,
    PERMISSION_RULE_LOCATION_POIS,
    PERMISSION_RULE_OWNERSHIP_ADMIN,
    PERMISSION_RULE_PURPOSE_RESEARCH,
    PERMISSION_RULE_PURPOSE_VALUATION,
    PERMISSION_RULE_REVISION,
    type PermissionRule,
} from "./util/permissions";
import {isResearchOnly} from "./util/purposeResolution";
import type {RevaPermission} from "./util/revaPermission";

interface SubRoute {
    /** URL to redirect to */
    url: string;
    /** optional permission to check */
    permission?: PermissionRule;
}

/**
 * Creates the browser router for the application.
 * Route loaders use the provided permissions to redirect index routes to the
 * first sub-route the user has access to.
 * @param permissions granted permissions of the logged-in user
 */
export const routerFactory = (permissions: RevaPermission[]) => {
    function getQueryString(request: Request): string {
        const url = new URL(request.url);
        return url.search ? url.search : "";
    }

    /**
     * select first available of existing sub-routes (or first, if all unavailable)
     */
    function redirectToFirstAvailableSubRoute(request: Request, subRoutes: SubRoute[]) {
        const queryString = getQueryString(request);

        for (const subRoute of subRoutes) {
            if (!subRoute.permission || checkPermissionRule(permissions, subRoute.permission)) {
                return redirect(subRoute.url + queryString);
            }
        }

        // fallback to first if none available
        return redirect(subRoutes[0].url + queryString);
    }

    /**
     * Loader guarding direct URL access to a permission-gated route (the tab bar hides such tabs
     * without the permission; a hand-typed URL must be redirected away too).
     * @param permission required permission rule
     * @param fallbackUrl where to redirect without the permission (its own index loader picks the tab)
     */
    function permissionLoader(permission: PermissionRule, fallbackUrl: string) {
        return ({request}: LoaderFunctionArgs) =>
            checkPermissionRule(permissions, permission) ? null : redirect(fallbackUrl + getQueryString(request));
    }

    // #19481: Research-only-User sehen auf der Startseite nur „Research-Abfragen".
    const researchOnly = isResearchOnly(permissions);

    /**
     * Loader for one of the five normal start-page tabs (#19125): a read-only revision user sees ONLY the exclusive
     * "Alle Bewertungen" tab, so any of the normal tabs (even for a revision user who additionally holds
     * reva-ownership-admin) bounces to /start/revision. Otherwise, if the tab is permission-gated and the user lacks
     * the permission, it bounces to /start (whose index loader then picks the first allowed tab); else it is allowed.
     * #19481: a research-only user sees ONLY the research tab — every other tab bounces to /start/research.
     * @param permission optional required permission rule of the tab
     * @param researchTab true for the research tab itself (the only tab a research-only user may open)
     */
    function startTabLoader(permission?: PermissionRule, researchTab = false) {
        return ({request}: LoaderFunctionArgs) => {
            const queryString = getQueryString(request);
            if (checkPermissionRule(permissions, PERMISSION_RULE_REVISION)) {
                return redirect(`/start/revision${queryString}`);
            }
            if (researchOnly && !researchTab) {
                return redirect(`/start/research${queryString}`);
            }
            if (permission && !checkPermissionRule(permissions, permission)) {
                return redirect(`/start${queryString}`);
            }
            return null;
        };
    }

    return createBrowserRouter([
        {
            path: "/",
            element: <App />,
            errorElement: <AppError />,
            children: [
                {
                    // The bare "/" is only a landing route: forward to the start page proper at /start,
                    // whose index loader then picks the first allowed tab. Everything that navigates to
                    // "/" (logout, reset, "no valid revaId" guard) keeps working through this hop.
                    index: true,
                    loader: ({request}: LoaderFunctionArgs) => redirect(`/start${getQueryString(request)}`),
                },
                {
                    path: "start",
                    element: <PageStart />,
                    children: [
                        {
                            index: true,
                            loader: ({request}: LoaderFunctionArgs) => {
                                // #19481: Research-only-User haben nur den Research-Reiter (Revision geht vor).
                                if (researchOnly && !checkPermissionRule(permissions, PERMISSION_RULE_REVISION)) {
                                    return redirect(`/start/research${getQueryString(request)}`);
                                }
                                return redirectToFirstAvailableSubRoute(request, [
                                    // #19125: revision-readonly users land on their exclusive tab; it is listed first
                                    // and gated, so only they match it — everyone else falls through to /start/wip.
                                    {url: "/start/revision", permission: PERMISSION_RULE_REVISION},
                                    {url: "/start/wip"},
                                    {url: "/start/pool"},
                                    {url: "/start/history"},
                                    {url: "/start/correction", permission: PERMISSION_RULE_CORRECTION},
                                    {url: "/start/teamadmin", permission: PERMISSION_RULE_OWNERSHIP_ADMIN},
                                    {url: "/start/research", permission: PERMISSION_RULE_PURPOSE_RESEARCH},
                                ]);
                            },
                        },
                        {
                            path: "wip",
                            // key pro Tab: /start/wip, /start/correction und /start/revision rendern alle
                            // <ValuationsTable/>, React würde die Instanz beim Wechsel sonst wiederverwenden (nur
                            // Prop-Wechsel, kein Remount) — dann liefe der Datenlade-Effekt (Mount == Tab-Aktivierung)
                            // nicht, und der neue Tab bliebe ungeladen (#19113).
                            loader: startTabLoader(),
                            element: <ValuationsTable key="WIP" tab="WIP" className="mt-2" />,
                        },
                        {
                            // #19481: Bewertungs-Einstieg (MK #19481-17) — zeigt „Meine Bewertungen", legt neue
                            // REVA-IDs aber ohne Rückfrage als Bewertung an (siehe entryPurposeForPath). Ohne
                            // reva-purpose-valuation zurück auf /start, dort gilt die übliche Regel.
                            path: "valuation",
                            loader: startTabLoader(PERMISSION_RULE_PURPOSE_VALUATION),
                            element: <ValuationsTable key="WIP" tab="WIP" className="mt-2" />,
                        },
                        {
                            path: "pool",
                            loader: startTabLoader(),
                            element: <PoolTab className="mt-2" />,
                        },
                        {
                            path: "history",
                            loader: startTabLoader(),
                            element: <DoneTab className="mt-2" />,
                        },
                        {
                            path: "correction",
                            loader: startTabLoader(PERMISSION_RULE_CORRECTION),
                            element: <ValuationsTable key="CORRECTION" tab="CORRECTION" className="mt-2" />,
                        },
                        {
                            path: "teamadmin",
                            loader: startTabLoader(PERMISSION_RULE_OWNERSHIP_ADMIN),
                            element: <AdminTab className="mt-2" />,
                        },
                        {
                            // #19481: „Research-Abfragen" — zugleich der Research-Einstieg: hier angelegte
                            // REVA-IDs sind ohne Rückfrage Research-Abfragen (siehe entryPurposeForPath).
                            path: "research",
                            loader: startTabLoader(PERMISSION_RULE_PURPOSE_RESEARCH, true),
                            element: <ValuationsTable key="RESEARCH" tab="RESEARCH" className="mt-2" />,
                        },
                        {
                            // #19125: exclusive read-only "Alle Bewertungen" tab — only for reva-lifecycle-revision-readonly.
                            path: "revision",
                            loader: permissionLoader(PERMISSION_RULE_REVISION, "/start"),
                            element: <ValuationsTable key="REVISION" tab="REVISION" className="mt-2" />,
                        },
                    ],
                },
                {
                    path: "chat",
                    loader: permissionLoader(PERMISSION_RULE_CHAT, "/start"),
                    element: <PageChat />,
                },
                {
                    path: "address",
                    element: <PageAddress />,
                },
                {
                    path: "location",
                    element: <PageLocation />,
                    children: [
                        {
                            index: true,
                            loader: ({request}: LoaderFunctionArgs) => {
                                return redirectToFirstAvailableSubRoute(request, [
                                    {
                                        url: "/location/points-of-interest",
                                        permission: PERMISSION_RULE_LOCATION_POIS,
                                    },
                                    {
                                        url: "/location/emission",
                                        permission: PERMISSION_RULE_EMISSION_TAB,
                                    },
                                    {
                                        url: "/location/climaterisk",
                                        permission: PERMISSION_RULE_CLIMATERISK,
                                    },
                                    {
                                        url: "/location/micro",
                                        permission: PERMISSION_RULE_LOCATION_MICRO,
                                    },
                                    {
                                        url: "/location/macro",
                                        permission: PERMISSION_RULE_LOCATION_MACRO,
                                    },
                                    {
                                        url: "/location/cadastre",
                                        permission: PERMISSION_RULE_CADASTRE,
                                    },
                                ]);
                            },
                        },
                        {
                            path: "points-of-interest",
                            element: <TabPoisLocation />,
                        },
                        {
                            path: "micro",
                            element: <TabMicroLocation />,
                        },
                        {
                            path: "macro",
                            element: <TabMacroLocation />,
                        },
                        {
                            path: "climaterisk",
                            element: <TabClimaterisk />,
                        },
                        {
                            path: "cadastre",
                            element: <TabCadastreLocation />,
                        },
                        {
                            path: "emission",
                            element: <TabEmission />,
                        },
                    ],
                },
                {
                    path: "market",
                    element: <PageMarket />,
                    children: [
                        {
                            index: true,
                            loader: ({request}: LoaderFunctionArgs) => {
                                return redirectToFirstAvailableSubRoute(request, [
                                    {
                                        url: "/market/description",
                                    },
                                ]);
                            },
                        },
                        {
                            path: "description",
                            element: <TabMarketDescription />,
                        },
                        {
                            path: "timelines",
                            element: <TabTimeLines />,
                        },
                        {
                            path: "priceindex",
                            element: <TabMarketPriceindex />,
                        },
                        {
                            path: "market-data",
                            element: <TabMarketData />,
                        },
                    ],
                },
                {
                    path: "object",
                    element: <PageObject />,
                    children: [
                        {
                            index: true,
                            loader: ({request}: LoaderFunctionArgs) => {
                                return redirectToFirstAvailableSubRoute(request, [
                                    {url: "/object/overview"},
                                    {url: "/object/register"},
                                    {url: "/object/energy"},
                                    {url: "/object/groundvalue"},
                                    {url: "/object/inspection"},
                                ]);
                            },
                        },
                        {
                            path: "overview",
                            element: <TabObjectOverview />,
                        },
                        {
                            path: "register",
                            element: <TabObjectRegister />,
                        },
                        {
                            path: "inspection",
                            element: <TabObjectInspection />,
                        },
                        {
                            path: "groundvalue",
                            element: <TabObjectGroundvalue />,
                        },
                        {
                            path: "energy",
                            element: <TabObjectEnergy />,
                        },
                        {
                            path: "initialValuation",
                            element: <TabObjectInitial />,
                        },
                        {
                            path: "finalize",
                            element: <TabObjectFinalize />,
                        },
                        {
                            path: "immodrop",
                            element: <TabObjectImmodrop />,
                        },
                    ],
                },
                {
                    path: "order",
                    element: <PageOrder />,
                },
                {
                    path: "value",
                    element: <PageValue />,
                    children: [
                        {
                            index: true,
                            loader: ({request}: LoaderFunctionArgs) => {
                                return redirectToFirstAvailableSubRoute(request, [
                                    {url: "/value/value"},
                                    {url: "/value/income-value"},
                                    {url: "/value/real-asset-value"},
                                    {url: "/value/comparative-value"},
                                    {url: "/value/energy-value"},
                                ]);
                            },
                        },
                        {
                            path: "value",
                            element: <TabValueIndication />,
                        },
                        {
                            path: "comparative-value",
                            element: <TabComparativeValueIndication />,
                        },
                        {
                            path: "income-value",
                            element: <TabIncomeValueIndication />,
                        },
                        {
                            path: "real-asset-value",
                            element: <TabRealAssetValueIndication />,
                        },
                        {
                            path: "energy-value",
                            element: <TabEnergyValueIndication />,
                        },
                    ],
                },
                {
                    path: "audit",
                    element: <PageAudit />,
                    children: [
                        {
                            index: true,
                            loader: ({request}: LoaderFunctionArgs) => {
                                return redirectToFirstAvailableSubRoute(request, [
                                    // Würdigungen hängen an der Appreciation-Berechtigung (pfandbg-16 ODER belwertv-24) (#18607)
                                    {url: "/audit/appreciations", permission: PERMISSION_RULE_APPRECIATIONS},
                                    {url: "/audit/documents"},
                                    {url: "/audit/changelog"},
                                ]);
                            },
                        },
                        {
                            path: "documents",
                            element: <TabDocuments />,
                        },
                        {
                            path: "appreciations",
                            element: <TabAppreciations />,
                        },
                        {
                            path: "changelog",
                            element: <TabChangelog />,
                        },
                    ],
                },
            ],
        },
    ]);
};
